import React, { useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { Form, Formik, FormikHelpers } from "formik"
import { toast } from "react-toastify"
import axios, { AxiosError } from "axios"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { IHttpErrorResponseModel, IPersonnelNdfDetailModel, IPersonnelNdfDocumentModel } from "@common-models/*"
import {
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MONTANT_INDEMNITE_PAR_KM,
     CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
     CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
} from "@common-constants/*"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"

// Sous-composants
import { NdfGroupedDetailsEditSection } from "./components/NdfGroupedDetailsEditSection"
import { NdfJustificatifsEditSection } from "./components/NdfJustificatifsEditSection"
import {
     addPersonnelNdfJustificatifRequest,
     deletePersonnelNdfJustificatifRequest,
     editPersonnelDepenseGroupedRequest,
     replacePersonnelNdfJustificatifRequest,
} from "../../../core/_requests"
import { useConnectedUserNdfDepensesContext } from "../../_PersonnelNdfDepenses"
import MyGoogleDriveFileViewerInModal from "../../../../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT } from "../../../../../../../endpoints"

const ConnectedUserNdfDepenseGroupedEdit = ({ detail }: { detail: IPersonnelNdfDetailModel }) => {
     const queryClient = useQueryClient()
     const context = useConnectedUserNdfDepensesContext()
     const MySwal = withReactContent(Swal)

     const [localDocuments, setLocalDocuments] = useState<IPersonnelNdfDocumentModel[]>(detail.personnelNdfDocuments || [])
     const [isLoadingDocAction, setIsLoadingDocAction] = useState(false)
     const [docToBeReplaced, setDocToBeReplaced] = useState<IPersonnelNdfDocumentModel>()
     const [justificatifInModal, setJustificatifInModal] = useState<IPersonnelNdfDocumentModel>()
     const refReplaceDocInput = useRef<HTMLInputElement>(null)
     const refAddDocInput = useRef<HTMLInputElement>(null)

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState<Record<number, any>>({})

     const validationSchema = Yup.object().shape({
          depenses: Yup.array().of(
               Yup.object().shape({
                    categorie: Yup.string().label("Catégorie").required("Champ requis").nullable(),
                    numeroTicket: Yup.number()
                         .label("Numéro de ticket")
                         .typeError("Format incorrect.")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired().nullable(),
                         }),
                    date: Yup.string()
                         .label("Date")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired().nullable(),
                         }),
                    montantTTC: Yup.number()
                         .label("Montant TTC")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    description: Yup.string()
                         .label("Commentaire")
                         .nullable()
                         .when("categorie", {
                              is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired(),
                         }),
                    // CHAMPS REPAS MIDI
                    repasSeulOuEnGroupe: Yup.string()
                         .label("Type de repas")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    invites: Yup.array()
                         .label("Invités")
                         .when("repasSeulOuEnGroupe", {
                              is: "REPAS_MIDI_EN_GROUPE",
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),

                    // CHAMPS INDEMNITÉ KM
                    moisAnneeKm: Yup.string()
                         .label("Mois")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Sélectionnez le mois"),
                         })
                         .nullable(),
                    joursKm: Yup.array()
                         .label("Jours")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.min(1, "Sélectionnez au moins un jour"),
                         })
                         .nullable(),
                    adresseDepart: Yup.string()
                         .label("Départ")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    adresseArrive: Yup.string()
                         .label("Arrivée")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    nbTrajets: Yup.number()
                         .label("Nombre de trajets")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    distanceTotale: Yup.number()
                         .label("Distance")
                         .when("categorie", {
                              is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    isAllerRetour: Yup.boolean().notRequired(),
               })
          ),
     })

     const initialValues = {
          depenses: detail.enfants!.map(item => ({
               id: item.id,
               date: item.date?.format("YYYY-MM-DD") || "",
               categorie: item.categorie,
               montantTTC: item.montantTtc || 0,
               description: item.description || "",
               numeroTicket: item.numeroTicket || "",
               repasSeulOuEnGroupe: item.invites?.length ? "REPAS_MIDI_EN_GROUPE" : "REPAS_MIDI_SEUL",
               invites: item.invites || [],
               adresseDepart: item.indemniteKmDetails?.addresseDepart || "",
               adresseArrive: item.indemniteKmDetails?.addresseArrive || "",
               nbTrajets: item.indemniteKmDetails?.nbTrajets || 0,
               isAllerRetour: item.indemniteKmDetails?.isAllerRetour || false,
               distanceTotale: item.indemniteKmDetails?.distanceKmTotal || 0,
               moisAnneeKm: item.date?.format("YYYY-MM") || "",
               joursKm: item.indemniteKmDetails?.joursKm?.map(Number) || [],
          })),
     }

     useEffect(() => {
          const kms: Record<number, any> = {}
          detail.enfants!.forEach((item, index) => {
               if (item.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM && item.indemniteKmDetails) {
                    kms[index] = {
                         distanceEnKmEntreDepartEtDestination: item.indemniteKmDetails.distanceKmEntreDepartEtArrive,
                         distanceTotal: item.indemniteKmDetails.distanceKmTotal,
                         montantTTC: Number((item.indemniteKmDetails.distanceKmTotal! * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
                    }
               }
          })
          setDetailsIndemniteKm(kms)
     }, [detail])

     const handleSubmit = (values: any, helpers: FormikHelpers<any>) => {
          helpers.setSubmitting(true)
          editPersonnelDepenseGroupedRequest(detail.id!, values)
               .then(() => {
                    queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                    toast.success("Modifications enregistrées.")
                    context.setCurrentDepenseInEdition(undefined)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key as any, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    helpers.setSubmitting(false)
               })
     }

     // --- Handlers Documents (Identiques au Simple Edit) ---
     const onAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file) {
               setIsLoadingDocAction(true)
               addPersonnelNdfJustificatifRequest(detail.id!, file)
                    .then(r => {
                         setLocalDocuments(prev => [...prev, r.data])
                         queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                    })
                    .finally(() => setIsLoadingDocAction(false))
          }
     }

     const handleRemoveDoc = (id: number, idx: number) => {
          MySwal.fire({ title: `Supprimer le n°${idx}?`, icon: "warning", showCancelButton: true }).then(r => {
               if (r.isConfirmed) {
                    setIsLoadingDocAction(true)
                    deletePersonnelNdfJustificatifRequest(id)
                         .then(() => {
                              setLocalDocuments(prev => prev.filter(d => d.id !== id))
                              queryClient.invalidateQueries([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId])
                         })
                         .finally(() => setIsLoadingDocAction(false))
               }
          })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
               {helpers => {
                    async function calculateDistance(index: number, departure?: string, arrival?: string) {
                         if (departure && arrival) {
                              return axios
                                   .post<{ distance: number | null }>(CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT, { departure, arrival })
                                   .then(({ data: { distance } }) => {
                                        if (distance) {
                                             setDetailsIndemniteKm(prev => ({
                                                  ...prev,
                                                  [index]: { ...prev[index], distanceEnKmEntreDepartEtDestination: distance },
                                             }))
                                        }
                                        return distance || 0
                                   })
                         }
                         return 0
                    }

                    function syncKM(index: number, dist: number, ar: boolean, nb: number) {
                         const dt = dist > 0 ? Number((dist * (ar ? nb * 2 : nb)).toFixed(2)) : 0
                         const montant = Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2))
                         setDetailsIndemniteKm(prev => ({
                              ...prev,
                              [index]: { ...prev[index], distanceTotal: dt, montantTTC: montant },
                         }))
                         helpers.setFieldValue(`depenses[${index}].distanceTotale`, dt)
                         helpers.setFieldValue(`depenses[${index}].montantTTC`, montant)
                    }

                    return (
                         <Form noValidate>
                              <div className="row g-9">
                                   <div className="col-lg-7 col-xl-8 order-2 order-lg-1">
                                        <NdfGroupedDetailsEditSection
                                             helpers={helpers}
                                             validationSchema={validationSchema}
                                             detailsIndemniteKm={detailsIndemniteKm}
                                             setDetailsIndemniteKm={setDetailsIndemniteKm}
                                             calculateDistance={calculateDistance}
                                             syncKM={syncKM}
                                        />
                                        <div className="separator separator-dashed my-6" />
                                        <div className="text-center d-flex justify-content-end">
                                             <button className="btn btn-sm btn-primary" type="submit" disabled={helpers.isSubmitting}>
                                                  {!helpers.isSubmitting ? (
                                                       <>
                                                            Enregistrer <i className="fas fa-check-circle ms-2" />
                                                       </>
                                                  ) : (
                                                       <MySimpleSpinner size="sm" />
                                                  )}
                                             </button>
                                        </div>
                                   </div>

                                   <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0">
                                        <div className="position-sticky" style={{ top: "25px" }}>
                                             <NdfJustificatifsEditSection
                                                  documents={localDocuments}
                                                  isLoading={isLoadingDocAction}
                                                  onAddClick={() => refAddDocInput.current?.click()}
                                                  onReplaceClick={doc => {
                                                       setDocToBeReplaced(doc)
                                                       refReplaceDocInput.current?.click()
                                                  }}
                                                  onRemoveClick={handleRemoveDoc}
                                                  onViewClick={setJustificatifInModal}
                                                  isKm={false}
                                             />
                                        </div>
                                   </div>
                              </div>
                              <input type="file" className="d-none" ref={refAddDocInput} onChange={onAddFile} />
                              <input
                                   type="file"
                                   className="d-none"
                                   ref={refReplaceDocInput}
                                   onChange={e => {
                                        const file = e.target.files?.[0]
                                        if (file && docToBeReplaced) {
                                             setIsLoadingDocAction(true)
                                             replacePersonnelNdfJustificatifRequest(docToBeReplaced.id!, file)
                                                  .then(r => {
                                                       setLocalDocuments(prev => prev.map(d => (d.id === docToBeReplaced.id ? r.data : d)))
                                                  })
                                                  .finally(() => setIsLoadingDocAction(false))
                                        }
                                   }}
                              />
                              {justificatifInModal && (
                                   <MyGoogleDriveFileViewerInModal
                                        googleDriveID={justificatifInModal.googleDriveID!}
                                        googleDriveMimetype={justificatifInModal.googleDriveMimeType!}
                                        modalTitle={<span>Justificatif</span>}
                                        handleCloseModal={() => setJustificatifInModal(undefined)}
                                   />
                              )}
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default ConnectedUserNdfDepenseGroupedEdit
